import React, { useEffect, useState, useMemo } from 'react';
import '../boatBooking.css';
import EditButton from '../../../ui/EditButton';
import { collection, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase-client';
import BoatBookingDashboardModal from '../../components/modals/rowing/BoatBookingDashboardModal';
import BoatCalendarModal from '../../components/modals/rowing/BoatCalendarModal';
import { CalendarButton } from '../../../ui/CalendarButton';
import {
  addDaysToDateISO,
  todayUKDateISO,
  ukDateISOToUTCEnd,
  ukDateISOToUTCStart
} from '../../../../utils/ukDates';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
function dayName(dow) {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dow] ?? '—';
}
function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function asJSDate(x) {
  if (!x) return null;
  if (x instanceof Date) return x;
  if (x?.toDate) return x.toDate();
  return new Date(x);
}
function fmtDate(ts) {
  const d = asJSDate(ts);
  if (!(d instanceof Date) || isNaN(d)) return '—';
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/London'
  });
}
function fmtTimeRange(fromTime, toTime) {
  if (!fromTime && !toTime) return 'All day';
  if (fromTime && toTime) return `${fromTime}–${toTime}`;
  if (fromTime) return `${fromTime}–?`;
  if (toTime) return `?–${toTime}`;
  return '—';
}
export default function BoatBookingCard({
  isAdmin,
  teamId,
  modules = {},
  rules = {},
  openCalendar = false,
  // { releaseDayOfWeek }
  onEditRules,
  onRequestBoat
}) {
  const enabled = !!modules?.rowing?.boatBooking?.enabled;
  const showCard = enabled;
  const [boats, setBoats] = useState([]);
  const [loadingBoats, setLoadingBoats] = useState(true);
  const [boatLoadError, setBoatLoadError] = useState('');
  const [boatRetryKey, setBoatRetryKey] = useState(0);

  // --- user requests state ---
  const [myReqs, setMyReqs] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [requestLoadError, setRequestLoadError] = useState('');
  const [requestRetryKey, setRequestRetryKey] = useState(0);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelError, setCancelError] = useState(null);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  useEffect(() => {
    if (openCalendar) setCalendarOpen(true);
  }, [openCalendar]);

  // --- filter state (defaults: next 7 days, inclusive) ---
  const defaultStartISO = todayUKDateISO();
  const defaultEndISO = addDaysToDateISO(defaultStartISO, 7);
  const [filterStartISO, setFilterStartISO] = useState(defaultStartISO);
  const [filterEndISO, setFilterEndISO] = useState(defaultEndISO);

  // Keep a live fleet listener so PWA reconnects recover without a full reload.
  useEffect(() => {
    if (!showCard || !teamId) return undefined;
    setLoadingBoats(true);
    setBoatLoadError('');
    return safeOnSnapshot(
      collection(db, `teams/${teamId}/boats`),
      snap => {
        const list = snap.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        setBoats(list);
        setLoadingBoats(false);
        setBoatLoadError('');
      },
      () => {
        setLoadingBoats(false);
        setBoatLoadError('Could not load the club boats. Check your connection and try again.');
      },
      {
        label: 'rowing-boat-booking-fleet',
        teamId,
        ignoreEmptyCacheSnapshot: true,
      }
    );
  }, [boatRetryKey, showCard, teamId]);

  // index boats by id for quick lookup
  const boatsById = useMemo(() => {
    const m = new Map();
    boats.forEach(b => m.set(b.id, b));
    return m;
  }, [boats]);
  const activeBoats = useMemo(() => boats.filter(b => (b?.status || 'active') !== 'maintenance'), [boats]);
  const canRequest = activeBoats.length > 0;
  const allocationDow = rules?.releaseDayOfWeek ?? 1; // default Monday

  // live-subscribe current user's requests in selected range
  useEffect(() => {
    if (!showCard || !teamId) return undefined;
    const uid = getAuth().currentUser?.uid;
    if (!uid) {
      setMyReqs([]);
      setLoadingReqs(false);
      setRequestLoadError('');
      return undefined;
    }

    // derive bounds from the filter controls
    const start = filterStartISO ? Timestamp.fromDate(ukDateISOToUTCStart(filterStartISO)) : Timestamp.fromDate(addDays(startOfToday(), -365)); // fallback
    const end = filterEndISO ? Timestamp.fromDate(ukDateISOToUTCEnd(filterEndISO)) : Timestamp.fromDate(addDays(startOfToday(), 365)); // fallback

    const qReq = query(collection(db, `teams/${teamId}/boatRequests`), where('memberId', '==', uid), where('date', '>=', start), where('date', '<=', end), orderBy('date', 'asc'), limit(100));
    setLoadingReqs(true);
    setRequestLoadError('');
    const unsubscribe = safeOnSnapshot(qReq, snap => {
      const rows = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setMyReqs(rows);
      setLoadingReqs(false);
      setRequestLoadError('');
    }, (error) => {
      setLoadingReqs(false);
      const code = String(error?.code || '').toLowerCase();
      setRequestLoadError(code === 'permission-denied' || code === 'firestore/permission-denied'
        ? 'Your booking requests could not be loaded because your account access is out of date. Ask a team administrator to refresh your boat-booking access.'
        : 'Could not load your booking requests. Check your connection and try again.');
    }, {
      label: 'rowing-boat-booking-member-requests',
      teamId,
      ignoreEmptyCacheSnapshot: true,
    });
    return () => unsubscribe();
  }, [showCard, teamId, filterStartISO, filterEndISO, requestRetryKey]);
  const statusClass = s => {
    const k = String(s || 'pending').toLowerCase();
    if (k === 'allocated' || k === 'confirmed') return 'status-pill success';
    if (k === 'unfilled' || k === 'cancelled') return 'status-pill danger';
    return 'status-pill info';
  };
  const statusLabel = s => {
    const k = String(s || 'pending').toLowerCase();
    if (k === 'allocated' || k === 'confirmed') return 'Booked';
    if (k === 'unfilled') return 'Unfilled';
    if (k === 'pending') return 'Pending';
    if (k === 'cancelled') return 'Cancelled';
    return k.charAt(0).toUpperCase() + k.slice(1);
  };
  async function handleCancel(req) {
    if (!teamId || !req?.id) return;
    setCancelError(null);
    const ok = window.confirm('Cancel this booking/request?');
    if (!ok) return;
    try {
      setCancellingId(req.id);
      const cancelFn = httpsCallable(functions, 'cancelBoatRequest');
      await cancelFn({
        teamId,
        requestId: req.id,
        soft: true
      });

      // Optimistic UI
      setMyReqs(prev => prev.filter(r => r.id !== req.id));
    } catch (e) {
      setCancelError('Sorry — could not cancel. Please try again.');
    } finally {
      setCancellingId(null);
    }
  }

  // derive filtered list
  const filteredReqs = useMemo(() => {
    const start = filterStartISO ? ukDateISOToUTCStart(filterStartISO) : null;
    const end = filterEndISO ? ukDateISOToUTCEnd(filterEndISO) : null;
    return myReqs.filter(r => {
      if (String(r.status || '').toLowerCase() === 'cancelled') return false; // hide cancelled

      const d = asJSDate(r.date);
      if (!(d instanceof Date) || isNaN(d)) return false;
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });
  }, [myReqs, filterStartISO, filterEndISO]);
  if (!showCard) return null;
  return <div className="team-card rowing-boat-booking-card boat-booking-card">
      <div className="card-header-with-icon">
        <span className="card-title">Boat Booking</span>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
          <CalendarButton compact collapseLabel onClick={() => setCalendarOpen(true)} aria-label="Open boat calendar" title="Open boat calendar">
            Calendar
          </CalendarButton>
          {isAdmin && <button type="button" className="btn btn--secondary btn--xs" onClick={() => setDashboardOpen(true)} aria-label="Open booking dashboard" title="Open booking dashboard">
              Dashboard
            </button>}
          {isAdmin && <EditButton section="boatBookingRules" onEdit={onEditRules} />}
        </div>
      </div>

      <div className="card-body">
        <div className="availability-status-list">
          <div className="availability-status-item">
            <div className="availability-status-label">
              <span className="availability-status-icon">📅</span><span>Allocation</span>
            </div>
            <span className="availability-pill available">{dayName(allocationDow)}</span>
          </div>

          <div className="availability-status-item">
            <div className="availability-status-label">
              <span className="availability-status-icon">🚣</span><span>Active boats</span>
            </div>
            <span className={`availability-pill ${canRequest ? 'available' : 'unavailable'}`}>
              {loadingBoats && boats.length === 0 ? 'Loading…' : boatLoadError && boats.length === 0 ? '—' : activeBoats.length}
            </span>
          </div>
        </div>

        <div style={{
        marginTop: 12,
        fontSize: '0.95rem'
      }}>
          Requests are collected and allocated weekly based on stated preferences and fair access.
          You can request any future date; confirmed bookings appear after the allocation run.
          Tagged override roles can be confirmed immediately when a suitable boat is free.
        </div>

        <div className="form-actions" style={{
        marginTop: 12
      }}>
          <button className="btn btn--primary" disabled={!canRequest || (loadingBoats && boats.length === 0)} onClick={onRequestBoat} type="button">
  Request a Boat
        </button>
          {!canRequest && !loadingBoats && !boatLoadError && <span className="text-muted" style={{
          marginLeft: 8
        }}>
              No active boats available
            </span>}
        </div>

        {boatLoadError && <div className="text-danger" role="alert" style={{
        marginTop: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap'
      }}>
            <span>{boatLoadError}</span>
            <button type="button" className="btn btn--secondary btn--xs" disabled={loadingBoats} onClick={() => setBoatRetryKey(value => value + 1)}>
              {loadingBoats ? 'Retrying…' : 'Retry'}
            </button>
          </div>}

        <section className="rowing-boat-booking-card__upcoming" aria-labelledby="rowing-upcoming-bookings-heading">
          <div className="rowing-boat-booking-card__upcoming-header boat-booking-card__upcoming-header">
            <h3 id="rowing-upcoming-bookings-heading" className="rowing-boat-booking-card__upcoming-title">
              Upcoming
            </h3>
            <div className="rowing-boat-booking-card__date-filters boat-booking-card__date-filters" aria-label="Booking date range">
              <label>
                <span>From</span>
                <input type="date" value={filterStartISO} onChange={e => setFilterStartISO(e.target.value)} />
              </label>
              <label>
                <span>To</span>
                <input type="date" value={filterEndISO} onChange={e => setFilterEndISO(e.target.value)} />
              </label>
            </div>
          </div>

        {loadingReqs && <div className="text-muted" style={{
          marginTop: 8
        }}>Loading your requests…</div>}
        {requestLoadError && <div className="text-danger" role="alert" style={{
          marginTop: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap'
        }}>
            <span>{requestLoadError}</span>
            <button type="button" className="btn btn--secondary btn--xs" disabled={loadingReqs} onClick={() => setRequestRetryKey(value => value + 1)}>
              {loadingReqs ? 'Retrying…' : 'Retry'}
            </button>
          </div>}
        {cancelError && <div className="text-danger" style={{
        marginTop: 8
      }}>{cancelError}</div>}

        <div className="rowing-boat-booking-card__request-list" style={{
        overflow: 'auto',
        paddingRight: 4,
        borderRadius: 8,
        border: '1px solid rgba(0,0,0,0.08)',
        background: 'var(--team-secondary, #f8f9fa)'
      }}>
          {!loadingReqs && !requestLoadError && filteredReqs.length === 0 && <div className="text-muted" style={{
          padding: '10px 12px'
        }}>
              No requests in this range.
            </div>}

          {!loadingReqs && filteredReqs.length > 0 && <ul className="list-clean" style={{
          display: 'grid',
          gap: 8,
          padding: 8
        }}>
              {filteredReqs.map(r => {
            const boatName = (r.selectedBoatId && boatsById.get(r.selectedBoatId)?.name) || (r.selectedBoatId ? 'Assigned boat' : null);
            const prefNames = Array.isArray(r.preferences) ? r.preferences.map(id => boatsById.get(id)?.name || 'Unavailable boat').slice(0, 2) : [];
            const isCancelling = cancellingId === r.id;
            return <li key={r.id} className="booking-item rowing-boat-booking-card__request-item boat-booking-card__request-item">
  <div className="rowing-boat-booking-card__request-details boat-booking-card__request-details">
    <div style={{
                  fontWeight: 600
                }}>
      {fmtDate(r.date)} • {fmtTimeRange(r.fromTime, r.toTime)}
    </div>
    <div className="text-muted" style={{
                  fontSize: '0.9rem'
                }}>
      {boatName ? <>Boat: <strong>{boatName}</strong></> : prefNames.length ? <>Prefs: {prefNames.join(', ')}{Array.isArray(r.preferences) && r.preferences.length > 2 ? '…' : ''}</> : '—'}
    </div>
  </div>

  {/* Actions pinned right */}
              <div className="row center rowing-boat-booking-card__request-actions boat-booking-card__request-actions">
  <span className={statusClass(r.status)}>{statusLabel(r.status)}</span>
  <button type="button" className="btn btn--secondary btn--xs" onClick={() => handleCancel(r)} disabled={isCancelling} aria-label="Cancel booking">
    {isCancelling ? 'Cancelling…' : 'Cancel'}
  </button>
              </div>
            </li>;
          })}
            </ul>}
        </div>
        </section>
      </div>

      {dashboardOpen && <BoatBookingDashboardModal isOpen={dashboardOpen} onClose={() => setDashboardOpen(false)} teamId={teamId} boats={boats} />}

      {calendarOpen && <BoatCalendarModal isOpen={calendarOpen} onClose={() => setCalendarOpen(false)} teamId={teamId} boats={boats} selectedBoatId={null} />}
    </div>;
}
